import { useState, type DragEvent, type KeyboardEvent } from "react";
import { GripVertical } from "lucide-react";
import clsx from "clsx";
import { Icon } from "../Icon/Icon.js";
import { dragType } from "./reorder.js";
import styles from "./Reorder.module.scss";

export interface DragHandleProps {
  /** What is being moved, for the accessible name ("Reorder Toured with Mae, 2 of 5"). */
  label: string;
  index: number;
  count: number;
  /** The list's kind — becomes `application/x-cntrl-<kind>`. Unique per list. */
  kind: string;
  /** Move the item. The caller owns the array; this only ever asks. */
  onMove: (from: number, to: number) => void;
  className?: string;
}

/**
 * The reorder affordance: a six-dot grip that is BOTH the pointer drag source and the keyboard control.
 *
 * Two things this shape buys, which the alternatives don't:
 * - **`draggable` lives on the handle, never the row**, so a row full of text inputs keeps its text
 *   selection — the reason we previously avoided row-drag entirely and shipped ↑/↓ pairs instead.
 * - **Arrow keys on the focused handle move the row**, so reordering is never pointer-only (HTML5 DnD
 *   has no keyboard equivalent). One control replaces the ↑/↓ pair without losing the keyboard path.
 */
export function DragHandle({ label, index, count, kind, onMove, className }: DragHandleProps) {
  const [announcement, setAnnouncement] = useState("");

  function move(to: number) {
    if (to < 0 || to >= count) return;
    onMove(index, to);
    setAnnouncement(`${label} moved to position ${to + 1} of ${count}.`);
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const to =
      e.key === "ArrowUp" || e.key === "ArrowLeft" ? index - 1
      : e.key === "ArrowDown" || e.key === "ArrowRight" ? index + 1
      : e.key === "Home" ? 0
      : e.key === "End" ? count - 1
      : null;
    if (to === null) return;
    e.preventDefault(); // arrows would otherwise scroll the page
    move(to);
  }

  return (
    <span className={clsx(styles.handleWrap, className)}>
      <button
        type="button"
        className={styles.handle}
        aria-label={`Reorder ${label}, ${index + 1} of ${count}`}
        draggable
        onDragStart={(e: DragEvent<HTMLButtonElement>) => {
          e.dataTransfer.setData(dragType(kind), String(index));
          e.dataTransfer.effectAllowed = "move";
        }}
        onKeyDown={onKeyDown}
      >
        <Icon icon={GripVertical} size={16} />
      </button>
      {/* Keyboard moves are silent otherwise — the handle keeps focus and nothing visibly changes near it. */}
      <span role="status" aria-live="polite" className={styles.srOnly}>
        {announcement}
      </span>
    </span>
  );
}
